<template>
    <div class="thread-input">
        <div
            class="thread-input-surface"
            :class="{ 'is-disabled': disabled }"
            @keydown="onKeydown"
        >
            <InlineMarkdownEditor
                v-model="message"
                class="thread-input-editor"
                placeholder="Type a message — / to reference"
                submit-on-enter
                :disabled="disabled"
                @submit="submit"
            />
            <Transition name="send-fade">
                <Button
                    v-if="canSend"
                    class="thread-input-send"
                    icon="pi pi-send"
                    aria-label="Send message"
                    rounded
                    @click="submit()"
                />
            </Transition>
        </div>
    </div>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import { computed, ref, watch } from "vue";

import InlineMarkdownEditor from "@/components/InlineMarkdownEditor.vue";

const props = defineProps<{
    disabled?: boolean;
}>();

const emit = defineEmits<{
    submit: [content: string];
    typing: [];
}>();

const message = ref("");

// Typing signal for the agent's hold-off (feat/thread-agent-typing-hold):
// watch the model, not DOM input events — the editor is a web component and
// the v-model contract is the reliable seam. Throttled; the post-send reset
// (empty value) is not typing.
const TYPING_EMIT_INTERVAL_MS = 3000;
let lastTypingEmit = 0;

watch(message, (value) => {
    if (!value) return;
    const now = Date.now();
    if (now - lastTypingEmit < TYPING_EMIT_INTERVAL_MS) return;
    lastTypingEmit = now;
    emit("typing");
});

// The send button only appears once there is something to send (chat-app
// convention) — Enter already sends, so it is a discoverability affordance
// rather than the primary path.
const canSend = computed(
    () => message.value.trim().length > 0 && !props.disabled
);

const submit = (content?: string) => {
    // The editor's submit event carries the live document (the v-model
    // update from the last keystroke can lag one tick behind); the
    // Ctrl/Cmd+Enter path falls back to the model.
    const text = (content ?? message.value).trim();
    if (!text || props.disabled) return;

    emit("submit", text);
    message.value = "";
};

// Enter sends via the editor's submit-on-enter; Ctrl/Cmd+Enter (the old
// shortcut) keeps working for muscle memory.
const onKeydown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        submit();
    }
};
</script>

<style scoped>
.thread-input {
    border-top: 1px solid var(--p-surface-200);
    background: var(--p-surface-0);
}

/* A single, borderless composer: press Enter to send. The send button fades
   in once there is a draft, as a discoverability affordance. */
.thread-input-surface {
    display: flex;
    align-items: flex-end;
    gap: 0.4rem;
    padding: 0.4rem 0.65rem;
    transition: background 0.15s ease;
}

.thread-input-surface:focus-within {
    background: var(--p-surface-50);
}

.thread-input-surface.is-disabled {
    opacity: 0.6;
}

.thread-input-editor {
    flex: 1;
    min-width: 0;
    max-height: 40vh;
    overflow-y: auto;
}

/* Force a square so the icon-only button renders a circle — PrimeVue's
   icon-only padding otherwise makes it taller than wide (oval). */
.thread-input-send {
    flex: 0 0 auto;
    box-sizing: border-box;
    width: 2rem !important;
    height: 2rem !important;
    min-width: 0 !important;
    padding: 0 !important;
}

.send-fade-enter-active,
.send-fade-leave-active {
    transition:
        opacity 0.15s ease,
        transform 0.15s ease;
}

.send-fade-enter-from,
.send-fade-leave-to {
    opacity: 0;
    transform: scale(0.8);
}
</style>
